import { Link, useParams } from 'react-router-dom';
import { useCallback } from 'react';
import { useCapture } from '../../data/queries/use-capture';
import { useCaptureWorkLog } from '../../data/queries/use-capture-work-log';
import { useProject } from '../../data/queries/use-project';
import { useSession } from '../auth/session';
import { formatInstant } from '../../lib/timezone';
import { CAPTURE_STATUS_LABEL, formatCaptureTitle } from './capture-status';
import { CapturePhotoSection } from '../photos/CapturePhotoSection';
import { CaptureWorkLog } from './CaptureWorkLog';
import { CaptureStatusControl } from './CaptureStatusControl';
import { CaptureTranscriptSection } from './CaptureTranscriptSection';
import { CaptureOrganizationSection } from './CaptureOrganizationSection';
import './captures.css';

/**
 * Capture Detail — M3 / S-03-02, with photo evidence added in M5 / S05-04.
 * Design export S12, reduced to what exists today.
 *
 * The transcript is CORRECTABLE since M6 / S06-03 — view-first, with a small
 * deliberate Edit and every previous accepted version preserved. Photos may be
 * added, reclassified and removed here at any Capture status (PT §9) and while
 * the parent Project is CLOSED (D-029); the same asymmetry holds for the
 * transcript, which stays editable inside a CLOSED Project.
 *
 * **THE TRANSCRIPT COMES FROM `capture_transcript_version`**, through
 * `useCapture`. It is never read from `work_log_event.detail.transcript`, which
 * holds a QUOTE for provenance display — a copy is allowed to fall behind the
 * thing it copied, and a memory product that renders the stale one is showing a
 * lie about what was said (D-016).
 *
 * THE LOCATION AND CAPTION ARE HERE as of M7 / S07-03, and both are
 * CORRECTABLE. The location reads in the title through the same formatter the
 * Capture list uses, so the two screens can never describe one Capture
 * differently; the caption sits in its own section, well away from the
 * transcript, because a derived summary must never be mistaken for what
 * Brandon actually said (PT §17). The deferred-work hint that used to close
 * this screen is gone with them — it disclaimed exactly these two things.
 *
 * DELIBERATELY ABSENT: revert (previous versions are readable, never
 * restorable by a button — there is no writer for it) · delete (not a v1
 * feature at all, PT §22 — photo removal is NON-DESTRUCTIVE and is not an
 * exception to this).
 *
 * The Work Log IS here as of M6 / S06-01, and it is read-only.
 *
 * TIMES RENDER IN THE CANONICAL CREWLINK TIMEZONE, never the device's
 * (INV-T17). The stored instant is UTC and is never rewritten; the profile's
 * timezone decides how it reads. When no profile timezone is available the time
 * is omitted rather than guessed — a wrong timestamp on a site record is worse
 * than a missing one.
 */
export function CaptureDetailScreen() {
  const { projectId: routeProjectId, captureId } = useParams<{
    projectId: string;
    captureId: string;
  }>();
  const { profile } = useSession();
  const {
    status,
    capture,
    transcript,
    transcriptVersion,
    transcriptVersions,
    organization,
    error,
    reload: reloadCapture,
  } = useCapture(captureId);

  /**
   * THE WORK LOG IS READ HERE, NOT INSIDE THE HISTORY SECTION (M6 / S06-02).
   *
   * The status control below mutates this Capture, and a status change has to
   * show up in BOTH places at once — the chip and the History. Owning both
   * queries at the screen keeps that to one function rather than a store, an
   * event bus, or a component reaching sideways into another.
   */
  const workLog = useCaptureWorkLog(captureId);
  const { reload: reloadWorkLog } = workLog;

  const reloadCaptureAndHistory = useCallback(async () => {
    await reloadCapture();
    await reloadWorkLog();
  }, [reloadCapture, reloadWorkLog]);

  /**
   * THE PROJECT CONTEXT COMES FROM THE LOADED CAPTURE, NOT THE ROUTE
   * (S-03-02 R1 finding F-3).
   *
   * The route's projectId is caller-supplied text. A crafted URL such as
   * `/projects/PROJECT-B/captures/CAPTURE-FROM-PROJECT-A` would otherwise
   * dress a real Capture in the wrong Project's name and back-link — RLS
   * stops any data leaking, but a memory product presenting a Capture under
   * the wrong Project is exactly the "filed in the wrong place" failure the
   * Capture screen states the Project to prevent. `capture.project_id` is
   * server truth, so once the Capture loads it is the only Project context
   * used. Until then no Project name is shown at all — the route id serves
   * only as a way back from the not-found and failed states, where no
   * Capture is being presented.
   */
  const contextProjectId = capture?.project_id;
  const { project } = useProject(contextProjectId);

  const timeZone = profile?.timezone ?? null;
  const backTarget = contextProjectId
    ? `/projects/${contextProjectId}`
    : routeProjectId
      ? `/projects/${routeProjectId}`
      : '/';
  const backLabel = project?.name ?? 'Project';

  return (
    <main className="cl-screen capture-detail">
      <p className="project-home-back">
        <Link className="cl-link" to={backTarget}>
          ‹ {backLabel}
        </Link>
      </p>

      {status === 'loading' ? (
        <p className="projects-loading" role="status">
          Loading this Capture…
        </p>
      ) : null}

      {/* Not found and not-yours are the SAME screen, on purpose. Saying "that
          exists but is not yours" would be the disclosure RLS exists to
          prevent — the same rule Project Home already follows. */}
      {status === 'not-found' ? (
        <section>
          <h1 className="cl-screen-title">Capture not available</h1>
          <p className="projects-empty-body">
            This Capture does not exist, or it is not yours. Go back and pick one from the list.
          </p>
        </section>
      ) : null}

      {status === 'failed' && error ? (
        <p className="cl-error" role="alert">
          {error}
        </p>
      ) : null}

      {status === 'ready' && capture ? (
        <>
          <header className="capture-detail-header">
            <h1 className="capture-detail-title">
              {/* The ACCEPTED location, from the same formatter the list uses,
                  so the two screens cannot describe one Capture differently
                  (M7 / S07-03). Never a guess — absent stays "Location not
                  set" (PT §5). */}
              {formatCaptureTitle(capture.capture_number, organization?.location_display)}
            </h1>
            <p className="capture-detail-meta">
              {timeZone ? formatInstant(capture.created_at, timeZone) : null}
            </p>
            <span className={`status-chip status-chip--capture-${capture.status}`}>
              {CAPTURE_STATUS_LABEL[capture.status]}
            </span>
          </header>

          {/* TRANSCRIPT — editable since M6 / S06-03, and correcting it
              refreshes BOTH this screen and the History below through the same
              reload the status control already uses. */}
          <CaptureTranscriptSection
            captureId={capture.id}
            transcript={transcript}
            version={transcriptVersion}
            // Everything after the head: the current version is what the
            // section renders as the transcript, so it cannot also appear in
            // its own history.
            previousVersions={transcriptVersions.slice(1)}
            onSaved={reloadCaptureAndHistory}
          />

          {/* PHOTOS ARE LIVE HERE — M5 / S05-04. The Capture is otherwise
              still read-only, and the hint below says exactly what remains
              deferred rather than a stale sentence that also disclaims photos.

              The id passed is `capture.id` — SERVER TRUTH, not the route's
              captureId — for the same reason the Project context is taken from
              the loaded row above (S-03-02 R1 finding F-3). */}
          {/* STATUS — M6 / S06-02. One tap, no confirmation, freely
              reversible, and it never touches the Project. */}
          <CaptureStatusControl
            captureId={capture.id}
            status={capture.status}
            onChanged={reloadCaptureAndHistory}
          />

          {/* WHAT CREWLINK WORKED OUT — M7 / S07-03. Below the transcript,
              because it is DERIVED from it and must never outrank it. Reading
              this screen organizes nothing; Organize and Retry are taps. */}
          <CaptureOrganizationSection
            captureId={capture.id}
            organization={organization}
            onChanged={reloadCaptureAndHistory}
          />

          <CapturePhotoSection captureId={capture.id} />

          {/* HISTORY — M6 / S06-01, READ ONLY. Same server-truth id as the
              photo section above, for the same reason (S-03-02 R1 F-3). */}
          <CaptureWorkLog workLog={workLog} />
        </>
      ) : null}
    </main>
  );
}
